import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
    useMemo,
    useRef,
} from 'react';
import {
    onAuthStateChanged,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signInWithPopup,
    linkWithCredential,
    OAuthProvider,
    signOut,
    sendPasswordResetEmail,
    sendEmailVerification,
} from 'firebase/auth';
import { auth, createMicrosoftProvider } from '@/shared/services/firebase';
import { userService } from '@/shared/services/userService';
import {
    authConfigService,
    DEFAULT_AUTH_CONFIG,
} from '@/shared/services/authConfigService';
import { isEmailVerified } from '@/shared/utils/emailVerified';

const AuthContext = createContext();

/**
 * Whether a 403 code is the server refusing the session rather than the
 * request.
 *
 * The sign-in policy refusals and a deactivated account are standing
 * decisions about the account, so retrying with this session can only repeat
 * them; each one ends the session instead of being reported on the page that
 * asked. An ordinary 403 is about one request and must not reach here.
 */
function endsTheSession(code) {
    return (
        typeof code === 'string' &&
        (code.startsWith('SIGN_IN_') || code === 'ACCOUNT_DEACTIVATED')
    );
}

// The server states the deactivation without saying what to do about it, and
// this notice is the only thing a deactivated member is told anywhere.
//
// Every refusal here ends the session on the login page, where the member has
// no account to act from and no page that names a human: the guidelines and
// their campus phone numbers sit behind authentication. So each one names the
// support address when the public config has supplied it, and falls back to
// the unaddressed sentence rather than to a mailto with nothing in it.
function deactivatedNotice(supportEmail) {
    return supportEmail
        ? 'This account has been deactivated. Email ' +
              supportEmail +
              ' to have it restored if you still need access.'
        : 'This account has been deactivated. Ask an exchange administrator' +
              ' to restore it if you still need access.';
}

function refusalNotice(data, supportEmail) {
    if (data.code === 'ACCOUNT_DEACTIVATED') {
        return deactivatedNotice(supportEmail);
    }
    const message = data.message || 'This sign-in method is not enabled here.';
    return supportEmail
        ? `${message} Email ${supportEmail} if you think that is wrong.`
        : message;
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hasProfile, setHasProfile] = useState(null);
    const [profileLoading, setProfileLoading] = useState(true);
    // True while a password sign-in is being followed by a Microsoft link.
    // The sign-in flips the global auth state a full round trip before the
    // link resolves; AuthRestrictedRoutes reads this to keep the login page
    // mounted until the outcome is known, so a failed link still has a page
    // to report to.
    const [linking, setLinking] = useState(false);
    // Mirror of `linking` for the profile effect, which must not fetch while
    // a link is in flight (the link step checks the session itself once the
    // outcome is known) and must not re-run merely because the flag flipped.
    const linkingRef = useRef(false);
    // The environment's sign-in policy, from the server. Until it arrives the
    // defaults offer both methods; the server enforces the real policy on
    // every request regardless, so the worst case is a method shown that the
    // server then refuses with a message.
    const [authConfig, setAuthConfig] = useState(DEFAULT_AUTH_CONFIG);
    // A sentence for the login page when the server refused a sign-in the
    // client had accepted: which method or email domain is not allowed here.
    const [authNotice, setAuthNotice] = useState('');
    // The same support address the state above carries, for the refusal path
    // alone. That path lives in a callback memoised with no dependencies, and
    // the config lands a round trip after mount, so reading the state there
    // would pin the empty default; naming the config as a dependency instead
    // would re-run the profile fetch the moment it resolves.
    const supportEmailRef = useRef(DEFAULT_AUTH_CONFIG.supportEmail);

    useEffect(() => {
        let cancelled = false;
        authConfigService
            .getAuthConfig()
            .then((remote) => {
                if (cancelled || !remote || !Array.isArray(remote.methods)) {
                    return;
                }
                const supportEmail =
                    typeof remote.supportEmail === 'string'
                        ? remote.supportEmail
                        : DEFAULT_AUTH_CONFIG.supportEmail;
                supportEmailRef.current = supportEmail;
                setAuthConfig({
                    methods: remote.methods,
                    password: {
                        allowedDomains: Array.isArray(
                            remote.password?.allowedDomains
                        )
                            ? remote.password.allowedDomains
                            : DEFAULT_AUTH_CONFIG.password.allowedDomains,
                    },
                    microsoft: {
                        tenant:
                            remote.microsoft?.tenant ||
                            DEFAULT_AUTH_CONFIG.microsoft.tenant,
                    },
                    supportEmail,
                });
            })
            .catch((error) => {
                console.error('Error fetching sign-in configuration:', error);
            });
        return () => {
            cancelled = true;
        };
    }, []);
    const profileFetchIdRef = useRef(0);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
            setCurrentUser(firebaseUser);
            setLoading(false);
            if (!firebaseUser) {
                setUserProfile(null);
                setHasProfile(null);
            }
        });
        return () => unsubscribe();
    }, []);

    // Loads the profile for a given user rather than the one in state, so a
    // step that changes what the same user object means (linking a provider
    // to an unverified password account makes it verified) can reload
    // without waiting for a state change that never comes.
    const loadProfile = useCallback(async (user) => {
        if (!isEmailVerified(user)) {
            setHasProfile(null);
            setUserProfile(null);
            setProfileLoading(false);
            return;
        }

        const fetchId = ++profileFetchIdRef.current;
        setProfileLoading(true);

        try {
            const profile = await userService.getProfile();
            if (fetchId !== profileFetchIdRef.current) return;
            setUserProfile(profile);
            setHasProfile(true);
            // A session the server accepts ends whatever a refusal said.
            setAuthNotice('');
            return { refused: false };
        } catch (error) {
            if (fetchId !== profileFetchIdRef.current) return;
            if (error.response && error.response.status === 404) {
                setHasProfile(false);
                setUserProfile(null);
            } else if (
                error.response &&
                error.response.status === 403 &&
                error.response.data?.code === 'EMAIL_NOT_VERIFIED'
            ) {
                // Only reachable when the client already treats the account
                // as verified, so the token most likely predates the
                // verification: the ID token the API sends is cached for up
                // to an hour. Refresh once and fetch again. If the server
                // still refuses, nothing this session can do will change
                // that, so it ends with a notice rather than leaving the
                // route guards waiting for a profile that never loads.
                let reloadOk = false;
                let retryError = null;
                try {
                    await user.reload();
                    reloadOk = true;
                    if (user.emailVerified) {
                        await user.getIdToken(true);
                        const profile = await userService.getProfile();
                        if (fetchId !== profileFetchIdRef.current) return;
                        setUserProfile(profile);
                        setHasProfile(true);
                        setAuthNotice('');
                        return { refused: false };
                    }
                } catch (err) {
                    retryError = err;
                }
                if (fetchId !== profileFetchIdRef.current) return;
                const retryStatus = retryError?.response?.status;
                if (retryStatus === 404) {
                    // Verified, and the API accepted the refreshed token;
                    // there is simply no profile yet.
                    setHasProfile(false);
                    setUserProfile(null);
                    return { refused: false };
                }
                const stillUnverified =
                    (reloadOk && !retryError && !user.emailVerified) ||
                    (retryStatus === 403 &&
                        retryError?.response?.data?.code ===
                            'EMAIL_NOT_VERIFIED');
                if (!stillUnverified) {
                    // A dropped connection or a server error during the retry
                    // says nothing about verification: leave the session
                    // alone, as the generic branch below does.
                    console.error(
                        'Error fetching user profile:',
                        retryError || error
                    );
                    setHasProfile(null);
                    setUserProfile(null);
                    return { refused: false };
                }
                setAuthNotice(
                    'Verify your email address from the message we sent, then sign in again.'
                );
                setHasProfile(null);
                setUserProfile(null);
                try {
                    await signOut(auth);
                } catch {
                    // The notice is what matters; a failed sign-out must not
                    // strand the page on a spinner.
                }
                setCurrentUser(null);
                return { refused: true };
            } else if (
                error.response &&
                error.response.status === 403 &&
                endsTheSession(error.response.data?.code)
            ) {
                // The server refused this session outright: the sign-in
                // method or email domain is not allowed here, or the account
                // has been deactivated. Nothing the person retries with this
                // session can change any of those, so end it and say why on
                // the login page.
                setAuthNotice(
                    refusalNotice(error.response.data, supportEmailRef.current)
                );
                setHasProfile(null);
                setUserProfile(null);
                try {
                    await signOut(auth);
                } catch {
                    // The notice is what matters; a failed sign-out must not
                    // strand the page on a spinner.
                }
                setCurrentUser(null);
                return { refused: true };
            } else {
                console.error('Error fetching user profile:', error);
                setHasProfile(null);
                setUserProfile(null);
            }
        } finally {
            if (fetchId === profileFetchIdRef.current) {
                setProfileLoading(false);
            }
        }
    }, []);

    const fetchProfile = useCallback(
        () => loadProfile(currentUser),
        [loadProfile, currentUser]
    );

    useEffect(() => {
        if (linkingRef.current) {
            return;
        }
        fetchProfile();
    }, [fetchProfile]);

    const signup = useCallback(async (email, password) => {
        const userCredential = await createUserWithEmailAndPassword(
            auth,
            email,
            password
        );
        await sendEmailVerification(userCredential.user);
        return userCredential;
    }, []);

    const login = useCallback(async (email, password) => {
        const userCredential = await signInWithEmailAndPassword(
            auth,
            email,
            password
        );
        // Force React state update — onAuthStateChanged may not re-fire on re-auth
        // with the same user, leaving stale emailVerified in state
        setCurrentUser(auth.currentUser);
        return userCredential;
    }, []);

    // Popup rather than redirect: the page keeps its state, and the result
    // arrives in the same session instead of on a reload the router has to
    // recover from. The provider's popup is a separate window, so the site's
    // frame policy does not apply to it.
    const loginWithMicrosoft = useCallback(async () => {
        let userCredential;
        try {
            userCredential = await signInWithPopup(
                auth,
                createMicrosoftProvider(authConfig.microsoft.tenant)
            );
        } catch (err) {
            // The address already belongs to a password account. Firebase
            // hands back the Microsoft credential it just obtained; keeping
            // it lets the login page link the two once the person proves the
            // password, instead of sending them away to start over.
            if (err?.code === 'auth/account-exists-with-different-credential') {
                const error = new Error(
                    'An account with this email already uses a password'
                );
                error.code = err.code;
                error.email = err.customData?.email;
                error.pendingCredential =
                    OAuthProvider.credentialFromError(err);
                throw error;
            }
            throw err;
        }
        // A Microsoft account is trusted as verified on the strength of the
        // provider, so the address it supplies is the whole identity. A
        // directory object with no mail attribute yields none, and letting
        // that account through would dead-end at profile creation.
        if (!userCredential.user?.email) {
            try {
                await signOut(auth);
            } catch (_signOutError) {
                // The person must see the missing-email message either way;
                // a failed sign-out is not a different problem to them.
            }
            const error = new Error(
                'Microsoft sign-in returned no email address'
            );
            error.code = 'auth/missing-email';
            throw error;
        }
        setCurrentUser(auth.currentUser);
        return userCredential;
    }, [authConfig]);

    // Proves ownership of the existing password account first; linking on an
    // email match alone would let a Microsoft account carrying a spoofed
    // address take over a profile. After this, both methods open one account.
    const linkMicrosoftToPasswordAccount = useCallback(
        async (email, password, pendingCredential) => {
            setLinking(true);
            linkingRef.current = true;
            try {
                const userCredential = await signInWithEmailAndPassword(
                    auth,
                    email,
                    password
                );
                try {
                    await linkWithCredential(
                        userCredential.user,
                        pendingCredential
                    );
                } catch (linkError) {
                    // The password was right but the link was refused. Leave
                    // the state as it was rather than half-done: signed out,
                    // with the reason shown on the login page. The stage tag
                    // lets the page tell an expired Microsoft credential
                    // apart from a wrong password, which share an error code.
                    try {
                        await signOut(auth);
                    } catch {
                        // The refused-link reason must reach the page either
                        // way; a failed sign-out is not what they need told.
                    }
                    linkError.linkStage = true;
                    throw linkError;
                }
                // The linked user is the same object React already holds, so
                // setting it again changes nothing; the profile is loaded for
                // it directly. An unverified password account becomes verified
                // through the new provider entry at this point.
                setCurrentUser(auth.currentUser);
                const outcome = await loadProfile(auth.currentUser);
                // In a Microsoft-only environment the link succeeded but this
                // session was opened with the password, and its token says
                // so; the server refuses it. The accounts are linked, which
                // is the news to give, and the next Microsoft sign-in works.
                if (outcome?.refused) {
                    setAuthNotice(
                        'Your accounts are linked. Sign in with your MSU Denver account.'
                    );
                    const error = new Error(
                        'Linked; a Microsoft sign-in is required here'
                    );
                    error.code = 'auth/linked-sign-in-required';
                    throw error;
                }
                return userCredential;
            } finally {
                linkingRef.current = false;
                setLinking(false);
            }
        },
        [loadProfile]
    );

    const clearAuthNotice = useCallback(() => setAuthNotice(''), []);

    const logout = useCallback(async () => {
        setUserProfile(null);
        setHasProfile(null);
        setAuthNotice('');
        return signOut(auth);
    }, []);

    const resetPassword = useCallback(
        (email) => sendPasswordResetEmail(auth, email),
        []
    );

    const reloadCurrentUser = useCallback(async () => {
        if (auth.currentUser) {
            await auth.currentUser.reload();
            // reload() refreshes the user record, not the ID token the API
            // sends; without a forced token refresh the server keeps seeing
            // email_verified: false for up to an hour after verification.
            await auth.currentUser.getIdToken(true);
            setCurrentUser(auth.currentUser);
            await loadProfile(auth.currentUser);
        }
    }, [loadProfile]);

    const refreshProfile = useCallback(() => {
        fetchProfile();
    }, [fetchProfile]);

    const value = useMemo(
        () => ({
            currentUser,
            userProfile,
            loading,
            hasProfile,
            profileLoading,
            linking,
            authMethods: authConfig.methods,
            passwordAllowedDomains: authConfig.password.allowedDomains,
            supportEmail: authConfig.supportEmail,
            authNotice,
            clearAuthNotice,
            signup,
            login,
            loginWithMicrosoft,
            linkMicrosoftToPasswordAccount,
            logout,
            resetPassword,
            reloadCurrentUser,
            fetchProfile,
            refreshProfile,
        }),
        [
            currentUser,
            userProfile,
            loading,
            hasProfile,
            profileLoading,
            linking,
            authConfig,
            authNotice,
            clearAuthNotice,
            signup,
            login,
            loginWithMicrosoft,
            linkMicrosoftToPasswordAccount,
            logout,
            resetPassword,
            reloadCurrentUser,
            fetchProfile,
            refreshProfile,
        ]
    );

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}

export default AuthContext;
